import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { useI18n } from '@/features/i18n/I18nProvider';
import { kuunda } from '@/lib/kuunda';
import {
  computeAccessState,
  hasFeatureAccess,
  isAllowedWhenLocked,
  type ActiveSubscription,
  type AccessState,
} from '@/features/subscription/access';

export function useAccessState() {
  const { profile, session, loading: authLoading, profileLoading, refreshProfile } = useAuth();
  const [subscription, setSubscription] = useState<ActiveSubscription | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);
  const recoveryAttempts = useRef(0);

  useEffect(() => {
    if (authLoading || profileLoading) {
      setLoaded(false);
      setProfileMissing(false);
      return;
    }

    if (!session) {
      setSubscription(null);
      setProfileMissing(false);
      setLoaded(true);
      return;
    }

    if (!profile) {
      setLoaded(false);
      if (recoveryAttempts.current < 2) {
        recoveryAttempts.current += 1;
        void refreshProfile();
        return;
      }
      setProfileMissing(true);
      setLoaded(true);
      return;
    }

    recoveryAttempts.current = 0;
    setProfileMissing(false);

    let cancelled = false;
    setLoaded(false);
    void kuunda
      .from('subscriptions')
      .select('plan_type, status, ends_on, installments_paid, installment_count, next_installment_at')
      .eq('user_id', profile.id)
      .eq('status', 'active')
      .gte('installments_paid', 1)
      .order('created_at', { ascending: false })
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          setSubscription(null);
          setLoaded(true);
          return;
        }
        const rows = (data ?? []) as ActiveSubscription[];
        setSubscription(rows[0] ?? null);
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [profile, session, authLoading, profileLoading, refreshProfile]);

  const state: AccessState = computeAccessState(profile, subscription);
  return { state, loaded, subscription, hasAccess: hasFeatureAccess(state), profileMissing };
}

export function RequireFeatureAccess({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const { t } = useI18n();
  const { refreshProfile } = useAuth();
  const { state, loaded, hasAccess, profileMissing } = useAccessState();

  if (!loaded) {
    return <div className="shell-boot" aria-busy="true" aria-label="Chargement" />;
  }

  if (profileMissing) {
    return (
      <div className="u-page">
        <section className="u-card">
          <h2>Profil introuvable</h2>
          <p>
            Votre compte est connecté mais votre profil n’a pas pu être chargé. Réessayez ou contactez le
            support ISBD.
          </p>
          <button type="button" className="btn btn-primary" onClick={() => void refreshProfile()}>
            Réessayer
          </button>
        </section>
      </div>
    );
  }

  if (hasAccess || isAllowedWhenLocked(location.pathname)) {
    return children ?? null;
  }

  if (location.pathname !== '/app/abonnement') {
    return <Navigate to="/app/abonnement" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="u-page">
      <section className="u-card">
        <h2>{t('access.lockedTitle')}</h2>
        <p>{t('access.lockedBody')}</p>
        <Link className="btn btn-primary" to="/app/abonnement">
          {t('access.goSubscribe')}
        </Link>
        <p className="muted" style={{ marginTop: '0.75rem' }}>
          {state.status === 'locked' && state.reason === 'subscription_ended'
            ? t('sub.subscriptionEndedBanner')
            : state.status === 'locked'
              ? t('sub.lockedBanner')
              : null}
        </p>
      </section>
    </div>
  );
}
